import { dateFormat, DateFormatType } from '@astronautsid/wpe-utils'

import type { ForecastProductListParamsType, FilterOptionType } from '../@types/forecastFilterTypes'

const DAYS_OPTIONS = [7, 14, 30]

const EXCLUDED_PERSIST_KEYS: ReadonlyArray<keyof ForecastProductListParamsType> = [
  'cursor',
  'page_size',
  'order_by',
  'order_direction',
  'need_review',
]

export type SavedOptionsType = {
  l1: FilterOptionType[]
  l2: FilterOptionType[]
  brand: FilterOptionType[]
  pic: FilterOptionType[]
  hub: FilterOptionType[]
  vendor: FilterOptionType[]
}

const pickPersistableFilters = (
  filters: ForecastProductListParamsType,
): Partial<ForecastProductListParamsType> => {
  const out: Partial<ForecastProductListParamsType> = {}
  ;(Object.keys(filters) as Array<keyof ForecastProductListParamsType>).forEach((key) => {
    if (EXCLUDED_PERSIST_KEYS.includes(key)) return
    const value = filters[key]
    if (value === undefined || value === null || value === '') return
    out[key] = value as never
  })
  return out
}

export const saveFiltersToStorage = (
  storageKey: string,
  filters: ForecastProductListParamsType,
  optionData: {
    categoryL1Options: FilterOptionType[]
    categoryL2FlatOptions: FilterOptionType[]
    picOptions: FilterOptionType[]
    hubOptions: FilterOptionType[]
    brandOptions: FilterOptionType[]
    vendorOptions?: FilterOptionType[]
  },
  prevSaved?: SavedOptionsType | null,
): SavedOptionsType => {
  const selectedL1Ids = (filters.l1_category_ids ?? '').split(',').filter(Boolean)
  const selectedL2Ids = (filters.l2_category_ids ?? '').split(',').filter(Boolean)
  const selectedBrandIds = (filters.brand_ids ?? '').split(',').filter(Boolean)
  const selectedPicEmails = (filters.pic_emails ?? '').split(',').filter(Boolean)
  const selectedHubId = filters.hub_id ?? ''
  const selectedVendorIds = ('vendor_ids' in filters ? filters.vendor_ids ?? '' : '')
    .split(',')
    .filter(Boolean)

  // Preserve a selected option that isn't in the current option list (e.g. only
  // present via augmentation) by falling back to the previously saved option.
  const resolveSelected = <T extends FilterOptionType>(
    selectedIds: string[],
    current: T[],
    prev: T[] | undefined,
  ): T[] =>
    selectedIds
      .map(
        (id) =>
          current.find((o) => String(o.value) === id) ?? prev?.find((o) => String(o.value) === id),
      )
      .filter((o): o is T => Boolean(o))

  const l1SelectedOptions = resolveSelected(
    selectedL1Ids,
    optionData.categoryL1Options,
    prevSaved?.l1,
  )
  const l2SelectedOptions = resolveSelected(
    selectedL2Ids,
    optionData.categoryL2FlatOptions,
    prevSaved?.l2,
  )
  const brandSelectedOptions = resolveSelected(
    selectedBrandIds,
    optionData.brandOptions,
    prevSaved?.brand,
  )
  const picSelectedOptions = resolveSelected(
    selectedPicEmails,
    optionData.picOptions,
    prevSaved?.pic,
  )
  const hubSelectedOptions = resolveSelected(
    selectedHubId ? [selectedHubId] : [],
    optionData.hubOptions,
    prevSaved?.hub,
  )
  const vendorSelectedOptions = resolveSelected(
    selectedVendorIds,
    optionData.vendorOptions ?? [],
    prevSaved?.vendor,
  )

  const savedOptions: SavedOptionsType = {
    l1: l1SelectedOptions,
    l2: l2SelectedOptions,
    brand: brandSelectedOptions,
    pic: picSelectedOptions,
    hub: hubSelectedOptions,
    vendor: vendorSelectedOptions,
  }

  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        filters: pickPersistableFilters(filters),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _savedOptions: savedOptions,
      }),
    )
  } catch (_e) {
    // noop — localStorage may be unavailable in some contexts
  }

  return savedOptions
}

export const loadFiltersFromStorage = (
  storageKey: string,
): {
  filters: Partial<ForecastProductListParamsType>
  savedOptions: SavedOptionsType | null
} => {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return { filters: {}, savedOptions: null }
    const parsed = JSON.parse(raw) as Record<string, unknown>

    const rawFilters = (parsed.filters as Record<string, unknown> | undefined) ?? {}
    const filters: Partial<ForecastProductListParamsType> = {}
    ;(Object.keys(rawFilters) as Array<keyof ForecastProductListParamsType>).forEach((key) => {
      if (EXCLUDED_PERSIST_KEYS.includes(key)) return
      const value = rawFilters[key]
      if (value === undefined || value === null || value === '') return
      filters[key] = value as never
    })

    // eslint-disable-next-line no-underscore-dangle
    const savedOptions = (parsed._savedOptions as SavedOptionsType) ?? null

    return { filters, savedOptions }
  } catch (_e) {
    return { filters: {}, savedOptions: null }
  }
}

export const clearFiltersFromStorage = (storageKey: string) => {
  try {
    localStorage.removeItem(storageKey)
  } catch (_e) {
    // noop
  }
}

export const toggleCheckboxValue = (currentCSV: string, toggledItem: string): string => {
  if (toggledItem === '') return ''

  const values = currentCSV.split(',').filter(Boolean)
  const index = values.indexOf(toggledItem)

  if (index > -1) {
    values.splice(index, 1)
  } else {
    values.push(toggledItem)
  }

  return values.join(',')
}

export const toStringOptions = (options: { label: string; value: string | number }[]) =>
  options.map((o) => ({ label: o.label, value: String(o.value) }))

export const formatDate = (date: Date, type: 'start' | 'end') =>
  dateFormat({ date, format: (type === 'start' ? 'DD MMM' : 'DD MMM YYYY') as DateFormatType })

export const defaultGapPeriod = () => {
  const today = new Date()
  const startDate = formatDate(new Date(today.getTime() - 7 * 86400000), 'start')
  const endDate = formatDate(new Date(today.getTime() + 13 * 86400000), 'end')

  return `${startDate} - ${endDate}`
}

export const getGapPeriodOptions = (type: 'past' | 'future') => {
  const today = new Date()
  const pastEnd = new Date(today.getTime() - 86400000)
  const futureStart = formatDate(today, 'start')
  const pastEndFormatted = formatDate(pastEnd, 'end')
  const futureEndBase = today.getTime()

  return DAYS_OPTIONS.map((days) => ({
    label: `${days}d`,
    displayLabel: `${type === 'past' ? 'Last' : 'Next'} ${days} Days`,
    startDate:
      type === 'past'
        ? formatDate(new Date(pastEnd.getTime() - (days - 1) * 86400000), 'start')
        : futureStart,
    endDate:
      type === 'past'
        ? pastEndFormatted
        : formatDate(new Date(futureEndBase + (days - 1) * 86400000), 'end'),
  }))
}
